import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { Logo } from '@/components/layout/logo'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { auth } from '@services/auth/auth-service'
import { AuthState } from '@/contexts/slices/auth'
import { useNavigate, useParams } from 'react-router-dom'
import { getInitials } from '@/utils'
import { billing } from '@/services/billing/billing-service'
import useAppState from '@/contexts/state'
import PaymentHeader from '../billing/payment-header'
import { Copy } from 'lucide-react'
import { Button } from '../ui/button'
import { useContext } from 'react'
import { billingContext } from '@/contexts/billing'
import { useTemporaryStore } from '@/contexts/temporaryStore'

const Header = () => {
  const { user, removeSession } = useAppState(state => ({
    user: (state as AuthState).user,
    removeSession: (state as AuthState).removeSession,
  }))

  const navigate = useNavigate()

  const logout = async () => {
    try {
      await auth.logout()
      removeSession()
      navigate('/login')
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <header className="sticky top-0 z-50">
      <div className="flex h-16 items-center justify-between border-b bg-background px-4 md:px-6">
        <nav className="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6">
          <a href="/" className="flex items-center gap-2 text-lg font-semibold md:text-base">
            <Logo className="h-6 w-6" />
            <span className="sr-only">Cobrief</span>
          </a>

          <StagingDropdown />
        </nav>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex cursor-pointer flex-row items-center">
              <Avatar>
                <AvatarFallback>{getInitials(user?.profile?.first_name, user?.profile?.last_name)}</AvatarFallback>
              </Avatar>
              <span className="sr-only">Toggle user menu</span>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <div className="mb-1 flex max-w-[240px] flex-col gap-0.5 border-b border-border px-3 pb-3 pt-1.5">
              <span
                className="truncate text-sm font-medium text-foreground/90"
                title={user?.profile?.first_name + ' ' + user?.profile?.last_name}
              >
                {user?.profile?.first_name + ' ' + user?.profile?.last_name}
              </span>
              <span className="truncate text-xs text-muted-foreground" title={user?.email}>
                {user?.email}
              </span>
            </div>

            <DropdownMenuItem onClick={billing.openStripePortal} className="cursor-pointer">
              Manage Billing
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" onClick={logout}>
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <PaymentHeader />
    </header>
  )
}

const StagingDropdown = () => {
  const params = useParams()
  const { subscription } = useContext(billingContext)
  const { currentDocumentMetadata, reviewMetadata } = useTemporaryStore(s => ({
    currentDocumentMetadata: s.currentDocumentMetadata,
    reviewMetadata: s.reviewMetadata,
  }))
  const { user } = useAppState(state => ({
    user: (state as AuthState).user,
  }))

  const documentPublicId = params.id
  const subscriptionId = subscription?.id

  const isStaging = typeof window !== 'undefined' && window.location.hostname.includes('staging.')
  const isLocal = typeof window !== 'undefined' && window.location.hostname.includes('localhost')
  if (!isStaging && !isLocal) return null

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log(`${label} copied to clipboard: ${text}`)
      })
      .catch(err => {
        console.error('Failed to copy:', err)
      })
  }

  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.round(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0')
    const seconds = (totalSeconds % 60).toString().padStart(2, '0')

    return `${minutes}:${seconds}`
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="default" className="h-[2rem]">
          {isStaging ? 'Staging' : 'Local'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[90vh] w-[500px] overflow-y-auto">
        <div className="flex flex-col p-1">
          {documentPublicId && (
            <div className="flex items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-muted/50">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-xs font-medium text-foreground">Document Public ID</span>
                <span className="truncate text-xs text-muted-foreground">{documentPublicId}</span>
              </div>
              <button
                className="flex-shrink-0 rounded p-1.5 hover:bg-muted"
                onClick={() => copyToClipboard(documentPublicId, 'Document Public ID')}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-muted/50">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">User ID</span>
              <span className="truncate text-xs text-muted-foreground">{user?.id || 'N/A'}</span>
            </div>
            <button
              className="flex-shrink-0 rounded p-1.5 hover:bg-muted"
              onClick={() => copyToClipboard(user?.id || 'N/A', 'User ID')}
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-muted/50">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">User Subscription ID</span>
              <span className="truncate text-xs text-muted-foreground">{subscriptionId || 'N/A'}</span>
            </div>
            <button
              className="flex-shrink-0 rounded p-1.5 hover:bg-muted"
              onClick={() => copyToClipboard(subscriptionId || 'N/A', 'User Subscription ID')}
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-muted/50">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">Subscription Metadata</span>
              <span className="truncate text-xs text-muted-foreground">
                {subscription?.metadata && Object.entries(subscription.metadata).length > 0
                  ? Object.entries(subscription.metadata)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join(', ')
                  : 'Empty'}
              </span>
            </div>
            <button
              className="flex-shrink-0 rounded p-1.5 hover:bg-muted"
              onClick={() =>
                copyToClipboard(
                  subscription?.metadata ? JSON.stringify(subscription.metadata) : 'N/A',
                  'Subscription Metadata'
                )
              }
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-muted/50">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">Document Metadata</span>
              <span className="truncate text-xs text-muted-foreground">
                {currentDocumentMetadata && Object.entries(currentDocumentMetadata).length > 0
                  ? Object.entries(currentDocumentMetadata)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join(', ')
                  : 'Empty'}
              </span>
            </div>
            <button
              className="flex-shrink-0 rounded p-1.5 hover:bg-muted"
              onClick={() =>
                copyToClipboard(
                  currentDocumentMetadata ? JSON.stringify(currentDocumentMetadata) : 'N/A',
                  'Document Metadata'
                )
              }
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Review Metadata Section */}
          {reviewMetadata && (
            <div className="mt-[0.5rem] rounded-lg border border-border bg-muted/30 p-3">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Last Review Metadata</h3>
                <button
                  className="rounded p-1 hover:bg-muted"
                  onClick={() => copyToClipboard(JSON.stringify(reviewMetadata, null, 2), 'Review Metadata')}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="space-y-2 text-xs">
                {/* Document Info */}
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Document Type:</span>
                    <span className="font-medium text-foreground">{reviewMetadata.detectedDocumentType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Duration:</span>
                    <span className="font-medium text-foreground">{reviewMetadata.duration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Test Mode:</span>
                    <span className="font-medium text-foreground">{reviewMetadata.testMode ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                {/* Blocks Analysis */}
                <div className="border-t border-border pt-2">
                  <p className="mb-1 font-medium text-foreground">Blocks Analysis</p>
                  <div className="space-y-1 pl-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total / Pre-analyzed / Analyzed:</span>
                      <span className="font-medium text-foreground">
                        {reviewMetadata.blocksParsed.split('(')[0]} {' -> '}
                        {reviewMetadata.blocksParsed.split('(')[1].replace(')', '')} {' -> '}
                        {reviewMetadata.blocksAnalyzed?.eligible || 0}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Timed Out / Retried / Retry Success:</span>
                      <span className="font-medium text-foreground">
                        {reviewMetadata.blocksAnalyzed?.timedOut || 0} / {reviewMetadata.blocksAnalyzed?.retried || 0} /{' '}
                        {reviewMetadata.blocksAnalyzed?.retriedSuccessful || 0}
                      </span>
                    </div>

                    {reviewMetadata.blocksAnalyzed?.failed > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Failed:</span>
                        <span className="font-medium text-red-600">{reviewMetadata.blocksAnalyzed.failed}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Risks Found */}
                <div className="border-t border-border pt-2">
                  <p className="mb-1 font-medium text-foreground">Risks Summary</p>
                  <div className="space-y-1 pl-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Risks Found / no-risk blocks:</span>
                      <span className="font-medium text-foreground">
                        {reviewMetadata.risksFound} / {reviewMetadata.blocksWithNoRisks}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Benchmark/Performance */}
                {reviewMetadata.benchmark && (
                  <div className="border-t border-border pt-2">
                    <p className="mb-1 font-medium text-foreground">Performance Benchmarks</p>
                    <div className="space-y-1 pl-2">
                      {/* Document Preprocessing */}
                      {reviewMetadata.benchmark.documentPreprocessing && (
                        <div className="flex justify-between text-[11px]">
                          <span className="text-muted-foreground">Document Preprocessing:</span>
                          <span className="font-mono font-medium text-foreground">
                            Avg: {formatDuration(reviewMetadata.benchmark.documentPreprocessing.avgDuration)} | Max:{' '}
                            {formatDuration(reviewMetadata.benchmark.documentPreprocessing.maxDuration)}
                          </span>
                        </div>
                      )}

                      {/* Block Preprocessing */}
                      {reviewMetadata.benchmark.blockPreprocessing && (
                        <div className="flex justify-between text-[11px]">
                          <span className="text-muted-foreground">Block Preprocessing:</span>
                          <span className="font-mono font-medium text-foreground">
                            Avg: {formatDuration(reviewMetadata.benchmark.blockPreprocessing.avgDuration)} | Max:{' '}
                            {formatDuration(reviewMetadata.benchmark.blockPreprocessing.maxDuration)}
                          </span>
                        </div>
                      )}

                      {/* Risk Analysis */}
                      {reviewMetadata.benchmark.riskAnalysis && (
                        <div className="flex justify-between text-[11px]">
                          <span className="text-muted-foreground">Risk Analysis:</span>
                          <span className="font-mono font-medium text-foreground">
                            Avg: {formatDuration(reviewMetadata.benchmark.riskAnalysis.avgDuration)} | Max:{' '}
                            {formatDuration(reviewMetadata.benchmark.riskAnalysis.maxDuration)}
                          </span>
                        </div>
                      )}

                      {/* DB Save Duration */}
                      {reviewMetadata.benchmark.db?.saveDuration && (
                        <div className="flex justify-between text-[11px]">
                          <span className="text-muted-foreground">DB Save Duration:</span>
                          <span className="font-mono font-medium text-foreground">
                            {formatDuration(reviewMetadata.benchmark.db.saveDuration)}
                          </span>
                        </div>
                      )}

                      {/* version parties */}
                      {reviewMetadata.versionParties && (
                        <div className="flex justify-between text-[11px]">
                          <span className="text-muted-foreground">Party Detection Duration:</span>
                          <span className="font-mono font-medium text-foreground">
                            {formatDuration(reviewMetadata.versionParties.executionDuration || 0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Version Parties */}
                {reviewMetadata.versionParties && (
                  <div className="border-t border-border pt-2">
                    <p className="mb-1 font-medium text-foreground">Party Detection</p>
                    <div className="space-y-1 pl-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Parties Detected:</span>
                        <span className="font-medium text-foreground">
                          {reviewMetadata.versionParties.detectedParties.join(', ') || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tier / Confidence:</span>
                        <span className="font-medium text-foreground">
                          {reviewMetadata.versionParties.tier} / {reviewMetadata.versionParties.confidence}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default Header
